import { useParams } from '@reach/router';
import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';

import { allowedYearRange } from '../../../../../components/Inputs/YearRange/YearRange';
import { api } from '../../../../../core/api';
import { BenchmarkResultsInputRequest } from '../../../../../generated/model/benchmarkResultsInputRequest';
import { RangeOflong } from '../../../../../generated/model/rangeOflong';
import { useErrorsDispatch } from '../../../../FinancialData/shared/ErrorContext/ErrorContext';
import { ErrorContextActionTypes } from '../../../../FinancialData/shared/ErrorContext/ErrorContext.types';
import { useBenchmarkState } from '../../../Context/BenchmarkContext';
import { tpPliList } from './ResultsInput/TpPliValues';

const FORM_SUBMISSION_DELAY_MS = 1000;

export interface ResultsInputsHookState {
  profitLevelIndicator: BenchmarkResultsInputRequest.ProfitLevelIndicatorEnum;
  indicatorType: BenchmarkResultsInputRequest.IndicatorTypeEnum;
  yearRange: RangeOflong;
  adjustments: BenchmarkResultsInputRequest.AdjustmentsEnum[];
  manufacturerOrDistributor?: BenchmarkResultsInputRequest.ManufacturerOrDistributorEnum;
  fiscalYearAlignment: boolean;
  isInputsLoading: boolean;
  testedPartyFile?: File;
  handleTpPliValueChange: (value: string) => void;
  handleIndicatorTypeValueChange: (value: React.FormEvent<HTMLInputElement>) => void;
  handleAdjustmentsChange: (
    key: BenchmarkResultsInputRequest.AdjustmentsEnum,
    isSelected: boolean,
  ) => void;
  handleFiscalAlignmentChange: (isSelected: boolean) => void;
  handleManufacturerOrDistributorChange: (value: React.FormEvent<HTMLInputElement>) => void;
  handleYearRangeValueChange: (value: RangeOflong) => void;
  setTestedPartyFile: (file?: File) => void;
  submitForm: (
    pli: BenchmarkResultsInputRequest.ProfitLevelIndicatorEnum,
    type: BenchmarkResultsInputRequest.IndicatorTypeEnum,
    range: RangeOflong,
    adjustments: BenchmarkResultsInputRequest.AdjustmentsEnum[],
    fiscalYearAlignment: boolean,
    manufacturerOrDistributor?: BenchmarkResultsInputRequest.ManufacturerOrDistributorEnum,
  ) => void;
}

export const useResultsInputs = (
  fetchResults: (formInput: BenchmarkResultsInputRequest) => void,
): ResultsInputsHookState => {
  const { benchmarkId } = useParams();
  const dispatchErrors = useErrorsDispatch();
  const { testedParty } = useBenchmarkState();

  const [profitLevelIndicator, setProfitLevelIndicator] = useState<
    BenchmarkResultsInputRequest.ProfitLevelIndicatorEnum
  >(tpPliList[0].value);

  const [indicatorType, setIndicatorType] = useState<
    BenchmarkResultsInputRequest.IndicatorTypeEnum
  >('WEIGHTED');
  const [adjustments, setAdjustments] = useState<BenchmarkResultsInputRequest.AdjustmentsEnum[]>(
    [],
  );
  const [manufacturerOrDistributor, setManufacturerOrDistributor] = useState<
    BenchmarkResultsInputRequest.ManufacturerOrDistributorEnum
  >();
  const [fiscalYearAlignment, setFiscalYearAlignment] = useState<boolean>(false);

  const [yearRange, setYearRange] = useState<RangeOflong>(allowedYearRange);

  const [testedPartyFile, setTestedPartyFile] = useState<File | undefined>();

  const [isInputsLoading, setInputsLoading] = useState(false);
  const [isInputsFetched, setIsInputsFetched] = useState(false);

  const submitForm = useCallback(
    _.debounce(
      (
        pli: BenchmarkResultsInputRequest.ProfitLevelIndicatorEnum,
        type: BenchmarkResultsInputRequest.IndicatorTypeEnum,
        range: RangeOflong,
        adjustments: BenchmarkResultsInputRequest.AdjustmentsEnum[],
        fiscalYearAlignment: boolean,
        manufacturerOrDistributor?: BenchmarkResultsInputRequest.ManufacturerOrDistributorEnum,
      ) => {
        fetchResults({
          profitLevelIndicator: pli,
          indicatorType: type,
          yearRange: range,
          adjustments: adjustments,
          manufacturerOrDistributor: manufacturerOrDistributor,
          fiscalYearAlignment: fiscalYearAlignment,
        });
      },
      FORM_SUBMISSION_DELAY_MS,
    ),
    [],
  );

  useEffect(() => {
    if (benchmarkId) {
      setInputsLoading(true);

      const fetchBenchmarkResultsInputs = async () => {
        try {
          const benchmarkInputPromise = api.benchmark.fetchBenchmarkResultsInputs(benchmarkId);
          const testedPartyPromise = !testedParty?.uploaded
            ? Promise.resolve()
            : api.benchmark.downloadTestedParty(benchmarkId).catch((error) => {
                dispatchErrors({
                  type: ErrorContextActionTypes.SetError,
                  payload: { error },
                });
              });

          const [data, testedPartyFile] = await Promise.all([
            benchmarkInputPromise,
            testedPartyPromise,
          ]);
          if (data) {
            const inputs = {
              profitLevelIndicator: data.profitLevelIndicator || tpPliList[0].value,
              indicatorType: data.indicatorType || 'WEIGHTED',
              yearRange: data.yearRange || allowedYearRange,
              adjustments: data.adjustments || [],
              manufacturerOrDistributor: data.manufacturerOrDistributor,
              fiscalYearAlignment: data.fiscalYearAlignment || false,
            };
            setProfitLevelIndicator(inputs.profitLevelIndicator!!);
            setIndicatorType(inputs.indicatorType!!);
            setYearRange(inputs.yearRange!!);
            setAdjustments(inputs.adjustments!!);
            setManufacturerOrDistributor(inputs.manufacturerOrDistributor);
            setFiscalYearAlignment(inputs.fiscalYearAlignment!!);

            fetchResults(inputs);
          }

          if (testedPartyFile) {
            setTestedPartyFile(testedPartyFile);
          }
        } catch (error) {
          dispatchErrors({
            type: ErrorContextActionTypes.SetError,
            payload: { error },
          });
        } finally {
          setInputsLoading(false);
          setIsInputsFetched(true);
        }
      };
      fetchBenchmarkResultsInputs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [benchmarkId]);

  const areRangeObjectsEqual = (range1: RangeOflong, range2: RangeOflong) => {
    return range1.to === range2.to && range1.from === range2.from;
  };

  const handleTpPliValueChange = (value: string) => {
    const val = value as BenchmarkResultsInputRequest.ProfitLevelIndicatorEnum;
    if (profitLevelIndicator === val) return; // handler should do nothing when same value is selected
    setProfitLevelIndicator(val);
    setAdjustments([]);
    if (val === 'RETURN_ON_ASSETS' || val === 'RETURN_ON_CAPITAL_EMPLOYED') {
      setManufacturerOrDistributor('MANUFACTURER');
    } else {
      setManufacturerOrDistributor(undefined);
    }
    submitForm(val, indicatorType, yearRange, [], fiscalYearAlignment);
  };

  const handleIndicatorTypeValueChange = (value: React.FormEvent<HTMLInputElement>) => {
    const val = value.currentTarget.value as BenchmarkResultsInputRequest.IndicatorTypeEnum;
    setIndicatorType(val);
    submitForm(
      profitLevelIndicator,
      val,
      yearRange,
      adjustments,
      fiscalYearAlignment,
      manufacturerOrDistributor,
    );
  };

  const handleManufacturerOrDistributorChange = (value: React.FormEvent<HTMLInputElement>) => {
    const val = value.currentTarget
      .value as BenchmarkResultsInputRequest.ManufacturerOrDistributorEnum;
    setManufacturerOrDistributor(val);
    submitForm(
      profitLevelIndicator,
      indicatorType,
      yearRange,
      adjustments,
      fiscalYearAlignment,
      val,
    );
  };

  const handleAdjustmentsChange = (
    key: BenchmarkResultsInputRequest.AdjustmentsEnum,
    isSelected: boolean,
  ) => {
    setAdjustments((prevState) => {
      const newAdjustments = isSelected
        ? [...prevState.filter((v) => v !== key), key]
        : [...prevState.filter((v) => v !== key)];
      submitForm(
        profitLevelIndicator,
        indicatorType,
        yearRange,
        newAdjustments,
        fiscalYearAlignment,
        manufacturerOrDistributor,
      );
      return newAdjustments;
    });
  };
  const handleFiscalAlignmentChange = (isSelected: boolean) => {
    setFiscalYearAlignment(isSelected);
    submitForm(
      profitLevelIndicator,
      indicatorType,
      yearRange,
      adjustments,
      isSelected,
      manufacturerOrDistributor,
    );
  };

  const handleYearRangeValueChange = (range: RangeOflong) => {
    const val = { from: range.from, to: range.to };
    if (areRangeObjectsEqual(val, yearRange)) return; // handler should do nothing when same values are selected
    setYearRange(val);
    // This handler gets triggered on default value set and when fetched from server. On these two cases we don't want
    // to auto-submit the form. We only auto-submit it when user interacts with it (after the data is fetched)
    if (isInputsFetched) {
      submitForm(
        profitLevelIndicator,
        indicatorType,
        val,
        adjustments,
        fiscalYearAlignment,
        manufacturerOrDistributor,
      );
    }
  };

  return {
    profitLevelIndicator,
    indicatorType,
    yearRange,
    adjustments,
    manufacturerOrDistributor,
    fiscalYearAlignment,
    isInputsLoading,
    testedPartyFile,
    handleTpPliValueChange,
    handleIndicatorTypeValueChange,
    handleAdjustmentsChange,
    handleFiscalAlignmentChange,
    handleManufacturerOrDistributorChange,
    handleYearRangeValueChange,
    setTestedPartyFile,
    submitForm,
  };
};
